/**
 * ThumbnailSpecV1 — Formalized SDUI contract for OG/Share image generation.
 *
 * Defines the schema for spec-driven image rendering via @json-render/image.
 * All image generation (OG images, share cards, thumbnails) flows through
 * this contract: input data → ThumbnailSpecV1 → renderToSvg → PNG.
 *
 * This schema is the SINGLE SOURCE OF TRUTH for what the spec-driven pipeline
 * accepts. buildOGImageSpec() produces output conforming to this schema.
 *
 * Aligned with @json-render/core Spec type:
 *   { root: string, elements: Record<string, SpecElement> }
 *
 * @see DRAMA-Sprint-12 Wave 2 — T5: Formalize ThumbnailSpecV1
 */

import { z } from "zod";

// ============================================================================
// Art Direction & Template Variants
// ============================================================================

/** The 4 template art direction families */
export const ART_DIRECTIONS = [
	"editorial-dark",
	"clean-analytic",
	"alert-high-contrast",
	"gradient-drama",
] as const;
export type ArtDirection = (typeof ART_DIRECTIONS)[number];

/** Emphasis mode: what the template prioritizes visually */
export const EMPHASIS_MODES = [
	"score-first",
	"narrative-first",
	"category-first",
] as const;
export type EmphasisMode = (typeof EMPHASIS_MODES)[number];

/** Layout format: determines canvas dimensions */
export const LAYOUT_FORMATS = [
	"horizontal",
	"square",
	"portrait",
	"reel",
] as const;
export type LayoutFormat = (typeof LAYOUT_FORMATS)[number];

/** Canvas dimensions by format */
export const FORMAT_DIMENSIONS: Record<
	LayoutFormat,
	{ width: number; height: number }
> = {
	horizontal: { width: 1200, height: 630 },
	square: { width: 1080, height: 1080 },
	portrait: { width: 1080, height: 1350 },
	reel: { width: 1080, height: 1920 },
};

// ============================================================================
// Spec Element Schema
// ============================================================================

/** A single element in the spec tree (mirrors @json-render/core SpecElement) */
export const specElementSchema = z.object({
	type: z.string(),
	props: z.record(z.string(), z.unknown()),
	children: z.array(z.string()),
});
export type SpecElement = z.infer<typeof specElementSchema>;

// ============================================================================
// ThumbnailSpecV1 Schema
// ============================================================================

/**
 * The complete spec contract for the image pipeline.
 *
 * Metadata provides rendering context that the pipeline needs beyond the
 * spec tree itself (art direction, format, content summary for caching keys).
 */
export const thumbnailSpecV1Schema = z.object({
	/** Schema version — always 1 for this contract */
	version: z.literal(1),

	/** Root element key — entry point for @json-render/image tree walk */
	root: z.string(),

	/** Flat element map — keys referenced by root and children arrays */
	elements: z.record(z.string(), specElementSchema),

	/** Rendering + decision metadata */
	metadata: z.object({
		/** Art direction family (drives color palette + visual tone) */
		artDirection: z.enum(ART_DIRECTIONS),

		/** Emphasis mode (drives element ordering + visual weight) */
		emphasisMode: z.enum(EMPHASIS_MODES),

		/** Layout format (drives canvas dimensions) */
		format: z.enum(LAYOUT_FORMATS),

		/** Surface this spec was built for */
		surface: z.enum(["og-image", "share-image"]),

		/** Template ID from the decision engine */
		templateId: z.string().optional(),

		/** Candidate ID from the decision engine */
		candidateId: z.string().optional(),

		/** Content snapshot for cache key derivation */
		content: z.object({
			/** Drama score (0-10) */
			score: z.number().min(0).max(10),

			/** Drama category */
			category: z.string(),

			/** Summary text (pre-truncated) */
			summary: z.string().optional(),

			/** Number of patterns detected */
			patternsCount: z.number().int().min(0).optional(),
		}),

		/** Generative image slot references (future: AI-generated backgrounds) */
		generativeSlots: z
			.array(
				z.object({
					/** Slot ID within the spec (matches an element key) */
					elementKey: z.string(),
					/** Generative asset type */
					assetType: z.enum(["background", "accent", "illustration"]),
					/** R2 key or URL for the generated asset */
					assetRef: z.string().optional(),
				}),
			)
			.optional(),

		/** Style token overrides (for A/B testing, theme experiments) */
		styleOverrides: z.record(z.string(), z.unknown()).optional(),
	}),
});

export type ThumbnailSpecV1 = z.infer<typeof thumbnailSpecV1Schema>;

// ============================================================================
// Type Guards & Helpers
// ============================================================================

/**
 * Validate and parse a raw object as ThumbnailSpecV1.
 * Throws a ZodError on invalid input.
 */
export function parseThumbnailSpec(raw: unknown): ThumbnailSpecV1 {
	return thumbnailSpecV1Schema.parse(raw);
}

/**
 * Validate without throwing — returns { success, data?, error? }.
 */
export function safeParseThumbnailSpec(raw: unknown) {
	return thumbnailSpecV1Schema.safeParse(raw);
}

/**
 * Extract the @json-render/core Spec shape from a ThumbnailSpecV1.
 * This is what renderToSvg() accepts as its first argument.
 */
export function toRenderSpec(spec: ThumbnailSpecV1): {
	root: string;
	elements: Record<string, SpecElement>;
} {
	return {
		root: spec.root,
		elements: spec.elements as Record<string, SpecElement>,
	};
}

/**
 * Get canvas dimensions for a ThumbnailSpecV1.
 */
export function getSpecDimensions(spec: ThumbnailSpecV1): {
	width: number;
	height: number;
} {
	return FORMAT_DIMENSIONS[spec.metadata.format];
}

/**
 * Derive a stable cache key from spec metadata.
 * Used for R2 object paths and CDN cache keys.
 */
export function deriveSpecCacheKey(spec: ThumbnailSpecV1): string {
	const m = spec.metadata;
	const parts = [
		m.surface,
		m.artDirection,
		m.emphasisMode,
		m.format,
		m.content.category,
		`s${m.content.score}`,
	];
	if (m.candidateId) parts.push(m.candidateId);
	return parts.join("/");
}
